'use client';

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useDynamicTheme } from '@/components/DynamicThemeProvider';
import { trackClick } from '@/services/api';
import { getServerUrl } from '@/utils/serverUrl';
import PromoModal from '@/components/coupon/PromoModal';

interface Props {
  coupon: any;
}

export default function CouponListCard({ coupon }: Props) {
  const [revealed, setRevealed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { siteConfig } = useDynamicTheme();
  const primary = siteConfig?.theme?.primaryColor || '#7c3aed';
  const serverUrl = getServerUrl();

  const store = coupon.store || {};
  const storeName = coupon.storeName || store.storeName || '';
  const rawLogo = coupon.storeLogo || store.logo || '';
  const storeLogo = rawLogo.startsWith('http') ? rawLogo : rawLogo ? `${serverUrl}${rawLogo}` : '';
  const storeUrl = coupon.affiliateUrl || coupon.websiteUrl || store.websiteUrl || '';
  const isExclusive = coupon.exclusive;
  const discount = coupon.discount || '';
  const code = coupon.code || '';

  const handleReveal = () => {
    setRevealed(true);
    if (storeUrl) window.open(storeUrl, '_blank', 'noopener,noreferrer');
    if (coupon._id) trackClick(coupon._id).catch(() => {});
    setShowModal(true);
  };

  return (
    <>
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200 overflow-hidden"
      style={{ boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px' }}>
      <div className="flex items-stretch">
        {/* Left: Logo / Discount */}
        <div className="w-40 sm:w-52 shrink-0 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-3 sm:p-6 gap-3">
          {isExclusive ? (
            <div className="text-center flex flex-col items-center w-full">
              <span className="block text-2xl sm:text-4xl font-black leading-tight uppercase" style={{ color: primary }}>EXCLUSIVE</span>
            </div>
          ) : discount ? (
            <div className="flex items-center justify-center px-4 py-5 sm:px-6 sm:py-7 min-w-[80px] sm:min-w-[110px] flex-shrink-0">
              <div className="flex w-fit flex-col self-center text-center font-black uppercase tracking-tight mx-auto text-2xl sm:text-4xl md:text-5xl" style={{ color: primary }}>
                {(() => {
                  const match = discount.match(/^(\d+)(%?\s*)(.*)?$/i);
                  if (match) {
                    const num = match[1];
                    const suffix = match[3]?.trim().replace(/off/i, '').trim();
                    return (
                      <>
                        <p className="leading-none">{num}%</p>
                        <p className="leading-none">Off</p>
                      </>
                    );
                  }
                  // Non-numeric like "FREE", "BOGO"
                  return <p className="leading-none">{discount}</p>;
                })()}
              </div>
            </div>
          ) : (
            <span className="block text-3xl sm:text-5xl font-black leading-none" style={{ color: primary }}>{storeName?.charAt(0) || '?'}</span>
          )}
        </div>

        {/* Middle: Info */}
        <div className="flex-1 px-5 sm:px-6 py-5 flex flex-col justify-center gap-2 min-w-0">
          {storeName && (
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: primary }}>{storeName}</span>
          )}
          {isExclusive && (
            <span className="inline-block text-white text-sm font-bold uppercase px-3 py-1.5 rounded w-fit tracking-wide" style={{ backgroundColor: '#14b8a6' }}>
              EXCLUSIVE
            </span>
          )}
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">{coupon.title}</h3>
          {coupon.details && (
            <p className="text-base text-gray-500 dark:text-gray-400 line-clamp-1">{coupon.details}</p>
          )}
          {coupon.tags && coupon.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {coupon.tags.map((tag: string, i: number) => (
                <span key={i} className="text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${primary}15`, color: primary }}>#{tag}</span>
              ))}
            </div>
          )}
        </div>

        {/* Right: Verified + CTA */}
        <div className="w-44 sm:w-56 shrink-0 flex flex-col items-end justify-center gap-3 px-5 sm:px-6 py-5">
          {code ? (
            <button
              onClick={handleReveal}
              className="text-white font-bold text-sm px-6 py-3.5 rounded-lg transition-all w-full text-center"
              style={{ backgroundColor: primary }}
            >
              SHOW CODE
            </button>
          ) : (
            <button
              onClick={handleReveal}
              className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold text-sm px-6 py-3.5 rounded-lg transition-colors w-full text-center"
            >
              GRAB DEAL
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end px-5 sm:px-6 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <a href={`/stores/${store.slug || storeName.toLowerCase().replace(/\s+/g, '-')}`}
          className="flex items-center gap-1 text-sm font-medium no-underline" style={{ color: primary }}>
          View All {storeName} Offers <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
    {showModal && (
      <PromoModal onClose={() => setShowModal(false)} title={coupon.title} code={code}
        discount={discount} storeName={storeName} storeLogo={storeLogo}
        storeUrl={storeUrl} expiryDate={coupon.expiryDate} details={coupon.details} />
    )}
    </>
  );
}